import { apiFetch } from "./client";
import type { ComplaintStatus } from "./complaints";
import type { QualityValue } from "./deliveries";

export type LotUsageTargetKind = "ORDER" | "COMPLAINT" | "PRODUCTION" | "OTHER";

export type ReceivingLot = {
  id: string;
  lotNumber: string;
  receivedOn: string;
  storageLocation: string | null;
  culture: string;
  note: string | null;
  /** Sum of the net weights in the lot — computed, never stored. */
  totalNetKg: number;
  deliveryCount: number;
  updatedAt: string;
};

export type LotDelivery = {
  deliveryId: string;
  companyId: string;
  companyName: string;
  announcedDate: string;
  netKg: number | null;
  weighbridgeTicketNo: string | null;
  quality: QualityValue[];
};

export type LotUsage = {
  id: string;
  usedOn: string;
  quantityKg: number;
  targetKind: LotUsageTargetKind;
  orderId: string | null;
  complaintId: string | null;
  externalReference: string | null;
  note: string | null;
};

/** A complaint linked to the lot — enough to recognise it and open it. */
export type LinkedComplaint = {
  id: string;
  caseNumber: string;
  companyName: string;
  title: string;
  status: ComplaintStatus;
  createdAt: string;
};

/**
 * The whole chain a lot can tell. The middle — production — lives in the ERP and
 * shows up here only as an external reference on a usage row.
 */
export type LotTraceability = {
  lot: ReceivingLot;
  deliveries: LotDelivery[];
  suppliers: string[];
  /** Weighted by delivered weight, not by delivery count. */
  averageQuality: QualityValue[];
  usage: LotUsage[];
  /** Complaints pointing at this lot; empty for a caller who may not read complaints. */
  complaints: LinkedComplaint[];
  totalNetKg: number;
  usedKg: number;
  remainingKg: number;
};

export const lotsApi = {
  list: () => apiFetch<ReceivingLot[]>("/lots"),
  get: (id: string) => apiFetch<LotTraceability>(`/lots/${id}`),
  create: (body: {
    lotNumber: string;
    receivedOn: string;
    storageLocation?: string;
    culture?: string;
    note?: string;
  }) => apiFetch<ReceivingLot>("/lots", { method: "POST", body }),
  assignDelivery: (id: string, deliveryId: string) =>
    apiFetch<void>(`/lots/${id}/deliveries/${deliveryId}`, { method: "POST" }),
  removeDelivery: (id: string, deliveryId: string) =>
    apiFetch<void>(`/lots/${id}/deliveries/${deliveryId}`, { method: "DELETE" }),
  recordUsage: (
    id: string,
    body: {
      usedOn: string;
      quantityKg: number;
      targetKind: LotUsageTargetKind;
      orderId?: string | null;
      complaintId?: string | null;
      externalReference?: string | null;
      note?: string | null;
    },
  ) => apiFetch<LotUsage>(`/lots/${id}/usage`, { method: "POST", body }),
};

/**
 * One lot that intake actually went into, read from the ERP intake notes.
 *
 * Not the same thing as `ReceivingLot`: that is a register the portal fills by hand and which stands
 * empty. A lot is not a truck — on this data it holds 79 intakes on average and up to 1.099 — so
 * `intakeCount` belongs beside every other number on the row.
 */
export type IntakeLot = {
  id: string;
  barcode: string;
  productName: string | null;
  firstIntake: string | null;
  lastIntake: string | null;
  intakeCount: number;
  netKg: number;
  analysisCount: number | null;
};

export const intakeLotsApi = {
  page: (strana: number, velicina: number, q?: string) => {
    const p = new URLSearchParams({ page: String(strana), size: String(velicina) });
    if (q && q.trim()) p.set("q", q.trim());
    return apiFetch<{
      content: IntakeLot[];
      totalElements: number;
      totalPages: number;
      number: number;
      size: number;
    }>(`/lots/intake?${p.toString()}`);
  },
};

export type IntakeLotDelivery = {
  id: string;
  erpId: string | null;
  receivedOn: string | null;
  companyName: string;
  netKg: number | null;
  vehiclePlate: string | null;
  status: string;
};

export type IntakeLotAnalysis = {
  id: string;
  analysedOn: string | null;
  controlPoint: string | null;
  testsChosenIndividually: boolean;
  legacyGroupId: number | null;
  productName: string | null;
  itemCount: number;
};

type Strana<T> = { content: T[]; totalElements: number; totalPages: number; number: number; size: number };

export const intakeLotDetailApi = {
  header: (id: string) => apiFetch<IntakeLot>(`/lots/intake/${id}`),
  deliveries: (id: string, strana: number, velicina: number) =>
    apiFetch<Strana<IntakeLotDelivery>>(`/lots/intake/${id}/deliveries?page=${strana}&size=${velicina}`),
  analyses: (id: string, strana: number, velicina: number) =>
    apiFetch<Strana<IntakeLotAnalysis>>(`/lots/intake/${id}/analyses?page=${strana}&size=${velicina}`),
};
